/**
 * Lina Storybook Gallery — fullscreen storybook viewer.
 * ---------------------------------------------
 * - Vanilla JS only (no jQuery, no frameworks).
 * - Everything lives inside an IIFE: no globals are leaked.
 * - Each section instance is created once per `.lina-storybook-gallery`
 *   element; Theme Editor section reload re-creates it automatically.
 * - Story pages are naturally sorted by the numeric part of the handle,
 *   e.g. storybook-c1, storybook-c2, ... storybook-c10 (never 1,10,11,2).
 */
(function () {
  'use strict';

  var DATA_PREFIX = 'LinaStorybookGalleryData-';
  var SWIPE_THRESHOLD = 48;

  function LinaStorybookGallery(sectionEl) {
    this.section = sectionEl;
    this.sectionId = sectionEl.getAttribute('data-section-id') || '';
    this.data = this.readData() || [];

    this.viewer = sectionEl.querySelector('[data-lina-storybook-viewer]');
    this.imageEl = sectionEl.querySelector('[data-lina-viewer-image]');
    this.prevBtn = sectionEl.querySelector('[data-lina-viewer-prev]');
    this.nextBtn = sectionEl.querySelector('[data-lina-viewer-next]');
    this.closeBtn = sectionEl.querySelector('[data-lina-viewer-close]');
    this.rotateBtn = sectionEl.querySelector('[data-lina-viewer-rotate]');
    this.columns = sectionEl.querySelectorAll('[data-lina-gallery-column]');

    this.currentChapter = 0;
    this.currentPage = 0;
    this.isOpen = false;

    // The theme's section-heading stores spaces as non-breaking spaces
    // (U+00A0), which make the browser treat the whole heading as one long
    // unbreakable word and split it mid-word on narrow screens. Normalize
    // them to regular spaces so whole words wrap naturally. Runs regardless
    // of whether the viewer has data.
    var headingEl = sectionEl.querySelector('.title-wrapper .heading');
    if (headingEl) {
      // Use textContent (decoded text) — innerHTML serializes U+00A0 back to
      // the "&nbsp;" entity, so a /\u00A0/ regex never matches it.
      headingEl.textContent = headingEl.textContent.replace(/\u00A0/g, ' ');
    }

    if (!this.viewer || !this.data.length) {
      return;
    }

    // Sort pages: cover (-0) first, then story pages naturally sorted.
    for (var i = 0; i < this.data.length; i++) {
      this.data[i].pages = this.sortStoryPages(this.data[i].pages || []);
    }

    this.renderColumns();
    this.bindColumns();
    this.bindViewer();
    this.bindSwipe();
    this.bindKeyboard();
  }

  /* ---------------------------------------------------------- *
   * Data
   * ---------------------------------------------------------- */

  LinaStorybookGallery.prototype.readData = function () {
    var el = document.getElementById(DATA_PREFIX + this.sectionId);
    if (!el) {
      return [];
    }
    try {
      var parsed = JSON.parse(el.textContent);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      if (parsed && Array.isArray(parsed.chapters)) {
        return parsed.chapters;
      }
      return [];
    } catch (err) {
      if (window.console) {
        console.warn('[Lina Storybook Gallery] Could not parse story data:', err);
      }
      return [];
    }
  };

  /* ---------------------------------------------------------- *
   * Natural sorting
   * ---------------------------------------------------------- */

  LinaStorybookGallery.prototype.naturalSort = function (pages) {
    function numericParts(item) {
      var handle = String((item && item.handle) || '');
      var matches = handle.match(/\d+/g);
      return matches ? matches.map(Number) : [];
    }

    function compareNums(a, b) {
      var len = Math.min(a.length, b.length);
      for (var i = 0; i < len; i++) {
        if (a[i] !== b[i]) {
          return a[i] - b[i];
        }
      }
      return a.length - b.length;
    }

    return pages.slice().sort(function (a, b) {
      return compareNums(numericParts(a), numericParts(b));
    });
  };

  /* ---------------------------------------------------------- *
   * Sort pages: cover page (-0) first, then the story pages
   * naturally sorted by the numeric suffix of their handle.
   * ---------------------------------------------------------- */

  LinaStorybookGallery.prototype.sortStoryPages = function (pages) {
    var cover = null;
    var storyPages = [];
    for (var i = 0; i < pages.length; i++) {
      var parts = String((pages[i] && pages[i].handle) || '').split('-');
      if (parts[parts.length - 1] === '0') {
        cover = pages[i];
      } else {
        storyPages.push(pages[i]);
      }
    }
    storyPages = this.naturalSort(storyPages);
    if (cover) {
      storyPages.unshift(cover);
    }
    return storyPages;
  };

  /* ---------------------------------------------------------- *
   * Columns — cover image + copy filled from the first page
   * ---------------------------------------------------------- */

  LinaStorybookGallery.prototype.renderColumns = function () {
    var self = this;
    var columns = Array.prototype.slice.call(this.columns);

    columns.forEach(function (column, index) {
      var chapter = self.data[index];
      if (!chapter) {
        return;
      }
      var pages = chapter.pages || [];
      // The cover is always index 0 after sortStoryPages (cover -0 first).
      var first = pages[0] || null;

      // Cover image = the "-0" page (falls back to the first page).
      var cover = column.querySelector('[data-lina-gallery-cover]');
      if (cover && first && first.image) {
        cover.src = first.image;
        if (first.imageAlt) {
          cover.alt = first.imageAlt;
        }
      }

      // Column copy: prefer the Metaobject content. If the field only
      // contains images we keep whatever Liquid rendered (fallback/static
      // text, or nothing) — we never invent copy.
      var copyEl = column.querySelector('[data-lina-gallery-copy]');
      if (copyEl && first && first.content) {
        copyEl.innerHTML = first.content;
      }
    });
  };

  LinaStorybookGallery.prototype.bindColumns = function () {
    var self = this;
    for (var i = 0; i < this.columns.length; i++) {
      var column = this.columns[i];

      column.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-chapter-index'), 10);
        if (!isNaN(idx)) {
          self.open(idx);
        }
      });

      // Column is now a <div> with role="button"; keep keyboard access.
      column.addEventListener('keydown', function (e) {
        var isAction = e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32;
        if (!isAction) {
          return;
        }
        e.preventDefault();
        var idx = parseInt(this.getAttribute('data-chapter-index'), 10);
        if (!isNaN(idx)) {
          self.open(idx);
        }
      });
    }
  };

  /* ---------------------------------------------------------- *
   * Viewer — open / close / navigate
   * ---------------------------------------------------------- */

  LinaStorybookGallery.prototype.bindViewer = function () {
    var self = this;
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function () {
        self.close();
      });
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.goTo(self.currentPage - 1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.goTo(self.currentPage + 1);
      });
    }
    if (this.rotateBtn) {
      this.rotateBtn.addEventListener('click', function () {
        // Toggle the 90° clockwise rotation (mobile landscape view).
        self.viewer.classList.toggle('is-rotated');
      });
    }
  };

  LinaStorybookGallery.prototype.open = function (chapterIndex) {
    var chapter = this.data[chapterIndex];
    if (!chapter || !chapter.pages || !chapter.pages.length) {
      return;
    }
    // Start from the first story page, skipping the cover page (-0).
    this.currentChapter = chapterIndex;
    this.currentPage = chapter.pages.length > 1 ? 1 : 0;
    this.isOpen = true;
    this.setImage(chapter.pages[this.currentPage]);
    this.updateNav();
    // Always start in portrait; reset any previous rotation.
    this.viewer.classList.remove('is-rotated');
    this.viewer.classList.add('is-open');
    this.viewer.setAttribute('aria-hidden', 'false');
  };

  LinaStorybookGallery.prototype.close = function () {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.viewer.classList.remove('is-open');
    this.viewer.setAttribute('aria-hidden', 'true');
  };

  LinaStorybookGallery.prototype.setImage = function (page) {
    if (!this.imageEl || !page) {
      return;
    }
    // Immediate switch — no animation between pages.
    this.imageEl.src = page.image || '';
    this.imageEl.alt = page.imageAlt || '';
  };

  LinaStorybookGallery.prototype.goTo = function (index) {
    var chapter = this.data[this.currentChapter];
    if (!chapter) {
      return;
    }
    var pages = chapter.pages || [];
    if (index < 0 || index >= pages.length || index === this.currentPage) {
      return; // no looping
    }
    this.currentPage = index;
    this.setImage(pages[index]);
    this.updateNav();
  };

  LinaStorybookGallery.prototype.updateNav = function () {
    var chapter = this.data[this.currentChapter];
    if (!chapter) {
      return;
    }
    var pages = chapter.pages || [];
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentPage <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentPage >= pages.length - 1;
    }
  };

  /* ---------------------------------------------------------- *
   * Mobile swipe — horizontal only, never blocks vertical scroll
   * ---------------------------------------------------------- */

  LinaStorybookGallery.prototype.bindSwipe = function () {
    var self = this;
    var startX = 0;
    var startY = 0;
    var tracking = false;

    this.viewer.addEventListener('touchstart', function (e) {
      var t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    }, { passive: true });

    this.viewer.addEventListener('touchmove', function (e) {
      if (!tracking) {
        return;
      }
      var t = e.touches[0];
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;
      // Only swallow clearly horizontal gestures.
      if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault();
      }
    }, { passive: false });

    this.viewer.addEventListener('touchend', function (e) {
      if (!tracking) {
        return;
      }
      tracking = false;
      var t = e.changedTouches[0];
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) {
        return;
      }
      if (dx < 0) {
        self.goTo(self.currentPage + 1); // swipe left -> next
      } else {
        self.goTo(self.currentPage - 1); // swipe right -> previous
      }
    }, { passive: true });
  };

  /* ---------------------------------------------------------- *
   * ESC to close (desktop). Only active while the viewer is open.
   * ---------------------------------------------------------- */

  LinaStorybookGallery.prototype.bindKeyboard = function () {
    var self = this;
    document.addEventListener('keydown', function (e) {
      if (!self.isOpen) {
        return;
      }
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        e.preventDefault();
        self.close();
      }
    });
  };

  /* ---------------------------------------------------------- *
   * Boot — every instance + Theme Editor reload support
   * ---------------------------------------------------------- */

  function initOne(el) {
    if (!el || el._linaGalleryInit) {
      return;
    }
    el._linaGalleryInit = new LinaStorybookGallery(el);
  }

  function initAll() {
    var els = document.querySelectorAll('[data-lina-storybook-gallery]');
    for (var i = 0; i < els.length; i++) {
      initOne(els[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor: re-initialize after a section reload, clean up on unload.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var sectionId = e.detail && e.detail.sectionId;
      if (!sectionId) {
        return;
      }
      var section = document.getElementById('shopify-section-' + sectionId);
      if (!section) {
        return;
      }
      initOne(section.querySelector('[data-lina-storybook-gallery]'));
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var sectionId = e.detail && e.detail.sectionId;
      if (!sectionId) {
        return;
      }
      var section = document.getElementById('shopify-section-' + sectionId);
      if (!section) {
        return;
      }
      var el = section.querySelector('[data-lina-storybook-gallery]');
      if (el) {
        el._linaGalleryInit = null; // DOM is removed with the section
      }
    });
  }
})();
